import React, { FC } from 'react';
import { NextEpisode, Progress } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useIntl } from 'react-intl';

import * as CardKit from '@/shared/components/CardKit';
import { formatDuration } from '@/shared/lib/formatTime';

import { useI18n } from '../../hooks/useI18n';

import styles from './WatchingProgress.module.css';

interface WatchingProgressContentProps {
    nextEpisode: NextEpisode | null;
    movieProgress: Progress | null;
}

interface MovieProgressArguments {
    currentTime: number | undefined;
    duration: number | undefined;
}

const MovieProgress = ({ currentTime, duration }: MovieProgressArguments) => {
    const intl = useIntl();

    return (
        <div className={styles.progress}>
            <CardKit.Progress
                type="inline"
                value={currentTime ?? 0}
                maxValue={duration ?? 0}
                text={formatDuration(intl, duration ?? 0)}
            />
        </div>
    );
};

export const WatchingProgressContent: FC<WatchingProgressContentProps> = ({
    nextEpisode,
    movieProgress,
}) => {
    const i18n = useI18n();

    if (nextEpisode) {
        if (nextEpisode?.fallback) {
            return null;
        }

        const seasonNumber = nextEpisode?.seasonNumber;
        const subtitle = seasonNumber ? i18n('season_number', { seasonNumber }) : '';

        const episodeTitle = nextEpisode?.title?.toString();
        const episodeNumber = nextEpisode?.episodeNumber;

        let title = '';

        if (episodeTitle) {
            title = `${episodeNumber}. ${episodeTitle}`;
        } else {
            title = i18n('episode_number', { episodeNumber });

            return (
                <div className={styles.serialProgress}>
                    {title && <div className={styles.title}>{title}</div>}
                    {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
                    {Boolean(nextEpisode?.progress?.currentTime) && (
                        <MovieProgress
                            currentTime={nextEpisode?.progress?.currentTime}
                            duration={nextEpisode?.progress?.duration}
                        />
                    )}
                </div>
            );
        }
    }

    if (!movieProgress?.duration) {
        return null;
    }

    return (
        <div className={styles.filmProgress}>
            {Boolean(movieProgress?.currentTime) && (
                <MovieProgress
                    currentTime={movieProgress?.currentTime}
                    duration={movieProgress?.duration}
                />
            )}
        </div>
    );
};
